import { inject, Injectable, Signal, signal } from '@angular/core';
import { Role } from '@shared/enums/role.enum';
import { GET_USER_BY_ID } from '@shared/graphql/RBS/queries/get-user-by-id.query';
import { UserByIdQueryResult } from '@shared/interfaces/rbs/user-by-id-query-result.interface';
import { UserByIdVariables } from '@shared/interfaces/rbs/user-by-id-variables.interface';
import { UserOrganization } from '@shared/interfaces/rbs/user-organization.interface';
import { UsersInOrg } from '@shared/interfaces/rbs/users-in-org.interface';
import { map, Observable, of, tap } from 'rxjs';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { ServerName } from 'src/app/core/graphql/enums/server-name.enum';
import { GraphQLService, ResultQuery } from 'src/app/core/graphql/services/graphql.service';

export const STORED_USER_ORGANIZATION_KEY = 'user-organization';

@Injectable({ providedIn: 'root' })
export class RbsService {
  private readonly graphQLService = inject(GraphQLService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly servername = ServerName.RBSGraphQL;

  private readonly _userOrganizationList = signal<UserOrganization[]>([]);
  private readonly _selectedUserOrganization = signal<UserOrganization | null>(null);

  set selectedUserOrganization(selectedUserOrganization: UserOrganization) {
    this._selectedUserOrganization.set(selectedUserOrganization);
    this.storeSelectedUserOrganization(selectedUserOrganization);
  }

  get selectedUserOrganization(): Signal<UserOrganization | null> {
    return this._selectedUserOrganization.asReadonly();
  }

  get userOrganizationList(): Signal<UserOrganization[]> {
    return this._userOrganizationList.asReadonly();
  }

  constructor() {
    this.authenticationService.refreshToken().subscribe();
  }

  fetchUserOrganizationList(): Observable<UserOrganization[]> {
    if (this._userOrganizationList().length > 0) {
      return of(this._userOrganizationList());
    }

    return this.getUserById(this.authenticationService.userId).pipe(
      map(({ data }) => this.mapUserOrganizationList(data.userById.usersInOrg)),
      tap(userOrganizationList => this._userOrganizationList.set(userOrganizationList))
    );
  }

  private getUserById(id: string): ResultQuery<UserByIdQueryResult> {
    const userByIdVariables: UserByIdVariables = { id };

    return this.graphQLService.query(
      {
        query: GET_USER_BY_ID,
        variables: userByIdVariables,
        fetchPolicy: 'cache-first',
      },
      this.servername
    );
  }

  private mapUserOrganizationList(userInOrgList: UsersInOrg[]): UserOrganization[] {
    const userOrganizationPerRole: UserOrganization[] = userInOrgList
      .flatMap(userInOrg => {
        const organization = userInOrg.org;
        const organizationRoles = userInOrg.orgUsersForOrgRole;

        if (!organizationRoles.length) {
          return null;
        }

        return organizationRoles.map(organizationRole => {
          return {
            organizationName: organization.orgName,
            tenantId: userInOrg.orgId,
            role: organizationRole.orgRole.serviceRole.name as Role,
            roleId: organizationRole.orgRole.serviceRoleId,
          };
        });
      })
      .filter(organization => !!organization);

    return userOrganizationPerRole;
  }

  verifyStoredUserOrganization(): void {
    const storedUserOrganization = sessionStorage.getItem(STORED_USER_ORGANIZATION_KEY);

    if (!storedUserOrganization) {
      return;
    }

    const organizationSaved: Pick<UserOrganization, 'roleId' | 'tenantId'> = JSON.parse(storedUserOrganization);
    const matchedUserOrganization = this._userOrganizationList().find(
      ({ roleId, tenantId }) => roleId === organizationSaved?.roleId && tenantId === organizationSaved.tenantId
    );

    if (matchedUserOrganization) {
      this.selectedUserOrganization = matchedUserOrganization;
      return;
    }

    sessionStorage.removeItem(STORED_USER_ORGANIZATION_KEY);
  }

  private storeSelectedUserOrganization(selectedUserOrganization: UserOrganization): void {
    const { tenantId, roleId } = selectedUserOrganization;
    sessionStorage.setItem(STORED_USER_ORGANIZATION_KEY, JSON.stringify({ tenantId, roleId }));
  }
}
